import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

/**
 * Filters string-based refetchQueries down to query names that currently have
 * an active observable query on the client.
 *
 * String names in refetchQueries only ever refetch active queries; a name with
 * no active observable is a no-op that triggers Apollo's "Unknown query named
 * ... requested in refetchQueries" invariant warning (TA-10233). Dropping
 * inactive names is therefore behavior-preserving and only silences the
 * warning.
 *
 * Returns the input unchanged when no client is available, so callers degrade
 * to the previous behavior rather than silently skipping refetches.
 */
export const filterActiveRefetchQueries = (
  client: ApolloClient<NormalizedCacheObject> | undefined,
  queryNames: string[]
): string[] => {
  if (!client || queryNames.length === 0) {
    return queryNames;
  }

  const activeQueryNames = new Set<string>();
  client.getObservableQueries('active').forEach((observableQuery) => {
    if (observableQuery.queryName) {
      activeQueryNames.add(observableQuery.queryName);
    }
  });

  return queryNames.filter((name) => activeQueryNames.has(name));
};
